import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { transformationEntity } from '@/config/entities/transformations';
import { createCRUDService } from '@/services/genericCRUD';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { toast } from 'react-hot-toast';
import { RocketLaunchIcon, Squares2X2Icon, CheckCircleIcon, ExclamationTriangleIcon, PencilIcon, Cog6ToothIcon, EyeIcon, PlusIcon } from '@heroicons/react/24/outline';
import { inchstoneService, InchstoneStats } from '@/services/inchstoneService';
import api from '@/services/api';
import { hierarchyService } from '@/services/hierarchyService';
import { TransformationCard, ViewSwitcher, CardGrid, ViewMode, FilterToolbar } from '@/components/cards';
import { useCardViewRefresh } from '@/hooks/useCardViewRefresh';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

const transformationService = createCRUDService(transformationEntity);

// Custom service that includes inchstone stats
const transformationServiceWithStats = {
  ...transformationService,
  getAll: async (params: any) => {
    try {
      // First get the transformations
      const response = await transformationService.getAll(params);
      const transformations = response.data || [];
      
      // Then get inchstone stats for each transformation
      const transformationsWithStats = await Promise.all(
        transformations.map(async (transformation: any) => {
          try {
            const inchstoneStats = await inchstoneService.getTransformationStats(transformation.id);
            return {
              ...transformation,
              inchstone_stats: inchstoneStats
            };
          } catch (error) {
            console.warn(`Failed to load inchstone stats for transformation ${transformation.id}:`, error);
            return {
              ...transformation,
              inchstone_stats: {
                total_inchstones: 0,
                completed_inchstones: 0,
                delayed_inchstones: 0,
                completion_percentage: 0
              }
            };
          }
        })
      );
      
      return {
        ...response,
        data: transformationsWithStats
      };
    } catch (error) {
      console.error('Error fetching transformations with inchstone stats:', error);
      throw error;
    }
  }
};

export const TransformationsPage: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const safeT = (key: string, fallback: string) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };
  
  // View mode state - default to cards
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('transformations-view-mode');
    return (saved === 'cards' || saved === 'table') ? saved : 'cards';
  });
  
  // Card view data state
  const [cardData, setCardData] = useState<any[]>([]);
  const [filteredCardData, setFilteredCardData] = useState<any[]>([]);
  const [cardLoading, setCardLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>('name');
  
  // Inchstone statistics state
  const [inchstoneStats, setInchstoneStats] = useState<InchstoneStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [totalTransformationsCount, setTotalTransformationsCount] = useState<number | null>(null);
  const [transformationStats, setTransformationStats] = useState<{ completed: number; delayed: number; completionRate: number } | null>(null);
  
  // Use generic card view refresh hook
  const { refreshKey, refresh } = useCardViewRefresh(viewMode);

  // Delete confirmation dialog state (shared for card and table views)
  const [transformationToDelete, setTransformationToDelete] = useState<any | null>(null);
  const [skipDeleteConfirmation, setSkipDeleteConfirmation] = useState(false);

  // Lens — "My department" (hierarchy-filtered) vs "My projects" (where user is owner/member/sponsor/stakeholder)
  const [lens, setLens] = useState<'department' | 'projects'>('department');

  // Roadmap filter (projects by roadmap)
  const [roadmaps, setRoadmaps] = useState<Array<{ id: string; name?: string }>>([]);
  const [roadmapFilterId, setRoadmapFilterId] = useState<string>('');

  // Hierarchy filters (organizational/business, functional, geographic) — only apply when lens = department
  const [orgHierarchyFilterId, setOrgHierarchyFilterId] = useState<string>('');
  const [funcHierarchyFilterId, setFuncHierarchyFilterId] = useState<string>('');
  const [geoHierarchyFilterId, setGeoHierarchyFilterId] = useState<string>('');
  const [orgHierarchyOptions, setOrgHierarchyOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [funcHierarchyOptions, setFuncHierarchyOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [geoHierarchyOptions, setGeoHierarchyOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [hierarchyDefaultsReady, setHierarchyDefaultsReady] = useState(false);

  // Fetch tenant roadmaps for filter dropdown
  useEffect(() => {
    api.get('/tenant/roadmaps')
      .then((res) => {
        const raw = (res.data as { success?: boolean; data?: unknown })?.success ? (res.data as { data: unknown }).data : res.data;
        const list = Array.isArray(raw) ? raw : [];
        setRoadmaps(list.map((r: { id: string; name?: string }) => ({ id: r.id, name: r.name || r.id })));
      })
      .catch(() => setRoadmaps([]));
  }, []);

  // Fetch hierarchy options (scoped) and user assignments; set default filter = user's place or root
  useEffect(() => {
    const loadOptionsAndDefaults = async () => {
      const [business, functional, geographic] = await Promise.all([
        hierarchyService.getBusinessHierarchyScoped(),
        hierarchyService.getFunctionalHierarchyScoped(),
        hierarchyService.getGeographicHierarchyScoped()
      ]);
      setOrgHierarchyOptions(business.map((h) => ({ value: h.value, label: h.label })));
      setFuncHierarchyOptions(functional.map((h) => ({ value: h.value, label: h.label })));
      setGeoHierarchyOptions(geographic.map((h) => ({ value: h.value, label: h.label })));

      let assignments: {
        business_hierarchy_id?: string | null;
        functional_hierarchy_id?: string | null;
        geographic_hierarchy_id?: string | null;
      } | null = null;
      if (user?.id) {
        try {
          const res = await api.get(`/users/hierarchy/assignments/${user.id}`);
          const data = res.data?.data ?? res.data;
          assignments = data ?? null;
        } catch {
          assignments = null;
        }
      }

      const orgValues = business.map((h) => h.value);
      const funcValues = functional.map((h) => h.value);
      const geoValues = geographic.map((h) => h.value);

      const defaultOrg =
        assignments?.business_hierarchy_id && orgValues.includes(assignments.business_hierarchy_id)
          ? assignments.business_hierarchy_id
          : business[0]?.value ?? '';
      const defaultFunc =
        assignments?.functional_hierarchy_id && funcValues.includes(assignments.functional_hierarchy_id)
          ? assignments.functional_hierarchy_id
          : functional[0]?.value ?? '';
      const defaultGeo =
        assignments?.geographic_hierarchy_id && geoValues.includes(assignments.geographic_hierarchy_id)
          ? assignments.geographic_hierarchy_id
          : geographic[0]?.value ?? '';

      setOrgHierarchyFilterId(defaultOrg);
      setFuncHierarchyFilterId(defaultFunc);
      setGeoHierarchyFilterId(defaultGeo);
      setHierarchyDefaultsReady(true);
    };
    loadOptionsAndDefaults().catch(() => {
      setHierarchyDefaultsReady(true);
    });
  }, [user?.id]);

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('transformations-view-mode', viewMode);
  }, [viewMode]);

  // Load card data when in card view (only after hierarchy defaults are set so first load uses filters)
  useEffect(() => {
    if (viewMode === 'cards' && hierarchyDefaultsReady) {
      loadCardData();
    }
  }, [viewMode, location.pathname, refreshKey, roadmapFilterId, lens, orgHierarchyFilterId, funcHierarchyFilterId, geoHierarchyFilterId, hierarchyDefaultsReady]);

  const loadCardData = async () => {
    try {
      setCardLoading(true);
      const params: Record<string, string | undefined> = {};
      if (roadmapFilterId) params.roadmap_id = roadmapFilterId;
      if (lens === 'department') {
        if (orgHierarchyFilterId) params.organizational_hierarchy_id = orgHierarchyFilterId;
        if (funcHierarchyFilterId) params.functional_hierarchy_id = funcHierarchyFilterId;
        if (geoHierarchyFilterId) params.geographic_hierarchy_id = geoHierarchyFilterId;
      }
      if (lens === 'projects') params.scope = 'my-projects';
      const response = await transformationServiceWithStats.getAll(params);
      const transformations = response.data || [];
      setCardData(transformations);
      applyFiltersAndSort(transformations, searchTerm, activeFilters, sortBy);
    } catch (error) {
      console.error('Error loading transformations for card view:', error);
      toast.error(t('transformation.errorLoadingTransformations'));
    } finally {
      setCardLoading(false);
    }
  };

  // Apply filters and sorting to card data
  const applyFiltersAndSort = (
    data: any[],
    search: string,
    filters: string[],
    sort: string
  ) => {
    let filtered = [...data];

    // Apply search
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.industry?.toLowerCase().includes(searchLower) ||
        item.functional_area?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filters
    if (filters.length > 0) {
      filtered = filtered.filter(item => {
        if (filters.includes('active') && (item.status === 'ACTIVE' || item.status === 'IN_PROGRESS')) return true;
        if (filters.includes('on-hold') && item.status === 'ON_HOLD') return true;
        if (filters.includes('completed') && item.status === 'COMPLETED') return true;
        if (filters.includes('my-items') && item.owner_id === user?.id) return true;
        if (filters.includes('requires-action')) {
          const hasDelayedInchstones = (item.inchstone_stats?.delayed_inchstones || 0) > 0;
          return hasDelayedInchstones || item.status === 'AT_RISK';
        }
        return false;
      });
    }

    // Apply sorting
    switch (sort) {
      case 'name':
        filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'date-newest':
        filtered.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        break;
      case 'date-oldest':
        filtered.sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
        break;
      case 'progress-high':
        filtered.sort((a, b) => {
          const aProgress = a.completion_percentage || a.inchstone_stats?.completion_percentage || 0;
          const bProgress = b.completion_percentage || b.inchstone_stats?.completion_percentage || 0;
          return bProgress - aProgress;
        });
        break;
      case 'progress-low':
        filtered.sort((a, b) => {
          const aProgress = a.completion_percentage || a.inchstone_stats?.completion_percentage || 0;
          const bProgress = b.completion_percentage || b.inchstone_stats?.completion_percentage || 0;
          return aProgress - bProgress;
        });
        break;
      case 'relevance':
      default:
        // Smart defaults: requires action first, then by progress
        filtered.sort((a, b) => {
          const aHasDelayed = (a.inchstone_stats?.delayed_inchstones || 0) > 0;
          const bHasDelayed = (b.inchstone_stats?.delayed_inchstones || 0) > 0;
          if (aHasDelayed && !bHasDelayed) return -1;
          if (!aHasDelayed && bHasDelayed) return 1;
          const aProgress = a.completion_percentage || a.inchstone_stats?.completion_percentage || 0;
          const bProgress = b.completion_percentage || b.inchstone_stats?.completion_percentage || 0;
          return bProgress - aProgress;
        });
        break;
    }

    setFilteredCardData(filtered);
  };

  // Update filtered data when search, filters, or sort changes
  useEffect(() => {
    if (viewMode === 'cards' && cardData.length > 0) {
      applyFiltersAndSort(cardData, searchTerm, activeFilters, sortBy);
    }
  }, [searchTerm, activeFilters, sortBy, cardData, viewMode]);
  

  const handleView = (item: any) => {
    // Navigate to detail page with viewMode flag (forces read-only regardless of permissions)
    navigate(`/transformations/${item.id}`, { state: { viewMode: true } });
  };

  const handleEdit = (item: any) => {
    // Navigate to form page to edit transformation metadata
    navigate(`/transformations/${item.id}/edit`);
  };

  const handleUpdate = (item: any) => {
    // Navigate to editable detail page (permissions determine editability)
    navigate(`/transformations/${item.id}`, { state: { viewMode: false } });
  };

  const handleCreate = () => {
    navigate('/transformations/create');
  };

  const handleDelete = async (item: any) => {
    try {
      await transformationService.delete(item.id);
      toast.success(t('transformation.transformationDeletedSuccessfully'));
      refresh(); // Refresh card view if in card mode
    } catch (error) {
      console.error('Error deleting transformation:', error);
      toast.error(t('transformation.errorDeletingTransformation'));
      throw error;
    }
  };

  const handleDeleteWithConfirm = (item: any) => {
    if (skipDeleteConfirmation) {
      handleDelete(item);
    } else {
      setTransformationToDelete(item);
    }
  };

  const handleConfirmDeleteTransformation = async () => {
    if (!transformationToDelete) return;
    await handleDelete(transformationToDelete);
  };

  const handleCopy = async (item: any) => {
    try {
      // Use the custom transformation copy endpoint (not generic CRUD)
      const response = await api.post(`/transformations/${item.id}/copy`);
      toast.success(t('transformation.transformationCopiedSuccessfully') || 'Transformation copied successfully');
      refresh(); // Refresh card view if in card mode
      return response.data;
    } catch (error: any) {
      console.error('Error copying transformation:', error);
      const errorMessage = error.response?.data?.message || error.message || t('transformation.errorCopyingTransformation') || 'Failed to copy transformation';
      toast.error(errorMessage);
      throw error;
    }
  };


  // Build params for API calls (roadmap + hierarchy when department, scope when projects)
  const listFilterParams = (): Record<string, string | number | undefined> => {
    const p: Record<string, string | number | undefined> = { limit: 2000 };
    if (roadmapFilterId) p.roadmap_id = roadmapFilterId;
    if (lens === 'department') {
      if (orgHierarchyFilterId) p.organizational_hierarchy_id = orgHierarchyFilterId;
      if (funcHierarchyFilterId) p.functional_hierarchy_id = funcHierarchyFilterId;
      if (geoHierarchyFilterId) p.geographic_hierarchy_id = geoHierarchyFilterId;
    }
    if (lens === 'projects') p.scope = 'my-projects';
    return p;
  };

  // Load inchstone statistics (respects roadmap and hierarchy filters; wait for defaults so first load is filtered)
  useEffect(() => {
    if (hierarchyDefaultsReady) {
      loadInchstoneStats();
    }
  }, [hierarchyDefaultsReady, roadmapFilterId, lens, orgHierarchyFilterId, funcHierarchyFilterId, geoHierarchyFilterId]);

  const loadInchstoneStats = async () => {
    try {
      setStatsLoading(true);
      const params = listFilterParams();
      const [listResponse, listWithStats] = await Promise.all([
        transformationService.getAll({ page: 1, limit: 1, ...params }),
        transformationServiceWithStats.getAll(params).catch(() => ({ data: [] }))
      ]);
      const total = listResponse?.pagination?.total;
      const totalCount = typeof total === 'number' ? total : 0;
      setTotalTransformationsCount(totalCount);
      const list = Array.isArray(listWithStats?.data) ? listWithStats.data : [];

      // Transformations row: from filtered list only
      const completed = list.filter((t: any) => t.status === 'COMPLETED').length;
      const delayed = list.filter((t: any) => (t.inchstone_stats?.delayed_inchstones || 0) > 0 || t.status === 'AT_RISK').length;
      const completionRate = totalCount > 0 ? Math.round((completed / totalCount) * 100) : 0;
      setTransformationStats({ completed, delayed, completionRate });

      // Inchstones row: aggregate from filtered transformations' inchstone_stats only
      let totalInchstones = 0;
      let completedInchstones = 0;
      let delayedInchstones = 0;
      list.forEach((t: any) => {
        const s = t.inchstone_stats;
        totalInchstones += s?.total_inchstones ?? 0;
        completedInchstones += s?.completed_inchstones ?? 0;
        delayedInchstones += s?.delayed_inchstones ?? 0;
      });
      const inchstoneCompletionPct = totalInchstones > 0 ? Math.round((100 * completedInchstones) / totalInchstones) : 0;
      setInchstoneStats({
        total_inchstones: totalInchstones,
        completed_inchstones: completedInchstones,
        delayed_inchstones: delayedInchstones,
        completion_percentage: inchstoneCompletionPct
      });
    } catch (error) {
      console.error('Error loading inchstone stats:', error);
      toast.error(t('transformation.failedToLoadInchstoneStatistics'));
    } finally {
      setStatsLoading(false);
    }
  };



  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-1">
            <h1 className="text-xl font-bold text-[var(--color-surface-text)]">
              {t('transformation.transformations')}
            </h1>
            <button
              type="button"
              onClick={handleCreate}
              className="inline-flex items-center justify-center px-4 py-2 bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] text-sm font-medium rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              {t('common.create')}
            </button>
          </div>
          <p className="text-sm text-[var(--color-secondary-text)]">
            {t('transformation.manageTransformations')}
          </p>
        </div>

          {/* Statistics: Row 1 = Transformations, Row 2 = Inchstones */}
          {(totalTransformationsCount !== null || transformationStats || inchstoneStats) && (
            <div className="space-y-4 mb-6">
              {/* Row 1: Transformations - Transformations, Completed, Delayed, Completion rate */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                  <div className="rounded-lg border border-[var(--color-border)] p-3 hover:bg-[var(--color-hover-bg)] group">
                    <div className="flex items-center gap-3">
                      <RocketLaunchIcon className="h-6 w-6 flex-shrink-0 text-[var(--color-info)] group-hover:text-[var(--color-info)]/80" />
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium text-[var(--color-secondary-text)] group-hover:text-[var(--color-surface-text)]">{t('transformation.transformations') || 'Transformations'}</span>
                        <span className="text-sm font-semibold text-[var(--color-surface-text)]">{statsLoading ? '...' : (totalTransformationsCount ?? 0)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg border border-[var(--color-border)] p-3 hover:bg-[var(--color-hover-bg)] group">
                    <div className="flex items-center gap-3">
                      <CheckCircleIcon className="h-6 w-6 flex-shrink-0 text-[var(--color-success)] group-hover:text-[var(--color-surface-text)]" />
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium text-[var(--color-secondary-text)] group-hover:text-[var(--color-surface-text)]">{t('transformation.completed') || 'Completed'}</span>
                        <span className="text-sm font-semibold text-[var(--color-surface-text)]">{statsLoading ? '...' : (transformationStats?.completed ?? 0)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg border border-[var(--color-border)] p-3 hover:bg-[var(--color-hover-bg)] group">
                    <div className="flex items-center gap-3">
                      <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-[var(--color-error)] group-hover:text-[var(--color-surface-text)]" />
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium text-[var(--color-secondary-text)] group-hover:text-[var(--color-surface-text)]">{t('transformation.delayed') || 'Delayed'}</span>
                        <span className="text-sm font-semibold text-[var(--color-surface-text)]">{statsLoading ? '...' : (transformationStats?.delayed ?? 0)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg border border-[var(--color-border)] p-3 hover:bg-[var(--color-hover-bg)] group">
                    <div className="flex items-center gap-3">
                      <div className="h-6 w-6 flex-shrink-0 bg-[var(--color-accent-bg)]/25 rounded-full flex items-center justify-center border border-[var(--color-accent-bg)]/50">
                        <span className="text-[var(--color-accent-bg)] font-semibold text-xs">%</span>
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium text-[var(--color-secondary-text)] group-hover:text-[var(--color-surface-text)]">{t('transformation.completionRate') || 'Completion rate'}</span>
                        <span className="text-sm font-semibold text-[var(--color-surface-text)]">{statsLoading ? '...' : `${transformationStats?.completionRate ?? 0}%`}</span>
                      </div>
                    </div>
                  </div>
              </div>

              {/* Row 2: Inchstones - Inchstones, Completed, Delayed, Completion rate */}
              {inchstoneStats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                    <div className="rounded-lg border border-[var(--color-border)] p-3 hover:bg-[var(--color-hover-bg)] group">
                      <div className="flex items-center gap-3">
                        <Squares2X2Icon className="h-6 w-6 flex-shrink-0 text-[var(--color-info)] group-hover:text-[var(--color-info)]/80" />
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-medium text-[var(--color-secondary-text)] group-hover:text-[var(--color-surface-text)]">{t('transformation.inchstones') || 'Inchstones'}</span>
                          <span className="text-sm font-semibold text-[var(--color-surface-text)]">{statsLoading ? '...' : inchstoneStats.total_inchstones}</span>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-lg border border-[var(--color-border)] p-3 hover:bg-[var(--color-hover-bg)] group">
                      <div className="flex items-center gap-3">
                        <CheckCircleIcon className="h-6 w-6 flex-shrink-0 text-[var(--color-success)] group-hover:text-[var(--color-surface-text)]" />
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-medium text-[var(--color-secondary-text)] group-hover:text-[var(--color-surface-text)]">{t('transformation.completed') || 'Completed'}</span>
                          <span className="text-sm font-semibold text-[var(--color-surface-text)]">{statsLoading ? '...' : inchstoneStats.completed_inchstones}</span>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-lg border border-[var(--color-border)] p-3 hover:bg-[var(--color-hover-bg)] group">
                      <div className="flex items-center gap-3">
                        <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-[var(--color-error)] group-hover:text-[var(--color-surface-text)]" />
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-medium text-[var(--color-secondary-text)] group-hover:text-[var(--color-surface-text)]">{t('transformation.delayed') || 'Delayed'}</span>
                          <span className="text-sm font-semibold text-[var(--color-surface-text)]">{statsLoading ? '...' : inchstoneStats.delayed_inchstones}</span>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-lg border border-[var(--color-border)] p-3 hover:bg-[var(--color-hover-bg)] group">
                      <div className="flex items-center gap-3">
                        <div className="h-6 w-6 flex-shrink-0 bg-[var(--color-accent-bg)]/25 rounded-full flex items-center justify-center border border-[var(--color-accent-bg)]/50">
                          <span className="text-[var(--color-accent-bg)] font-semibold text-xs">%</span>
                        </div>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-medium text-[var(--color-secondary-text)] group-hover:text-[var(--color-surface-text)]">{t('transformation.completionRate') || 'Completion rate'}</span>
                          <span className="text-sm font-semibold text-[var(--color-surface-text)]">{statsLoading ? '...' : `${inchstoneStats.completion_percentage}%`}</span>
                        </div>
                      </div>
                    </div>
                </div>
              )}
            </div>
          )}

          {/* Phase 5.1: Lens tabs — My projects / My department — and hierarchy filters on same line */}
          <div className="flex items-center gap-3 flex-wrap mb-3">
            <button
              type="button"
              onClick={() => setLens('projects')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                lens === 'projects'
                  ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                  : 'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-[var(--color-surface-text)] hover:bg-[var(--color-accent-bg)]/10'
              }`}
            >
              My projects
            </button>
            <button
              type="button"
              onClick={() => setLens('department')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                lens === 'department'
                  ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
                  : 'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-[var(--color-surface-text)] hover:bg-[var(--color-accent-bg)]/10'
              }`}
            >
              My department
            </button>
            {lens === 'department' && (
              <>
            <select
              value={orgHierarchyFilterId}
              onChange={(e) => setOrgHierarchyFilterId(e.target.value)}
              className="px-4 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent appearance-none pr-8 min-w-[160px] shrink-0"
              title={t('transformation.businessHierarchy') || 'Organizational'}
            >
              <option value="">{safeT('transformation.allOrganizational', 'All organizational')}</option>
              {orgHierarchyOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              value={funcHierarchyFilterId}
              onChange={(e) => setFuncHierarchyFilterId(e.target.value)}
              className="px-4 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent appearance-none pr-8 min-w-[160px] shrink-0"
              title={t('transformation.functionalHierarchy') || 'Functional'}
            >
              <option value="">{safeT('transformation.allFunctional', 'All functional')}</option>
              {funcHierarchyOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              value={geoHierarchyFilterId}
              onChange={(e) => setGeoHierarchyFilterId(e.target.value)}
              className="px-4 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent appearance-none pr-8 min-w-[160px] shrink-0"
              title={t('transformation.geographicHierarchy') || 'Geographic'}
            >
              <option value="">{safeT('transformation.allGeographic', 'All geographic')}</option>
              {geoHierarchyOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
              </>
            )}
          </div>

          {/* Toolbar: Roadmaps | Search | Sort | Table/Cards — shown for both table and card view */}
          <div className="mb-4">
            <FilterToolbar
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder={t('transformation.searchTransformations') || 'Search transformations...'}
              controlsBeforeSearch={
                <select
                  value={roadmapFilterId}
                  onChange={(e) => setRoadmapFilterId(e.target.value)}
                  className="px-4 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent appearance-none pr-8 min-w-[160px] shrink-0"
                >
                  <option value="">All roadmaps</option>
                  {roadmaps.map((r) => (
                    <option key={r.id} value={r.id}>{r.name || r.id}</option>
                  ))}
                </select>
              }
              quickFilters={[
                { id: 'active', label: 'Active', value: 'active' },
                { id: 'on-hold', label: 'On Hold', value: 'on-hold' },
                { id: 'completed', label: 'Completed', value: 'completed' },
                { id: 'my-items', label: 'My Items', value: 'my-items' },
                { id: 'requires-action', label: 'Requires Action', value: 'requires-action' }
              ]}
              activeFilters={activeFilters}
              onFilterToggle={(filterId) => {
                setActiveFilters(prev =>
                  prev.includes(filterId)
                    ? prev.filter(id => id !== filterId)
                    : [...prev, filterId]
                );
              }}
              sortOptions={[
                { id: 'relevance', label: 'Relevance', value: 'relevance' },
                { id: 'name', label: 'Name (A-Z)', value: 'name' },
                { id: 'date-newest', label: 'Date (Newest)', value: 'date-newest' },
                { id: 'date-oldest', label: 'Date (Oldest)', value: 'date-oldest' },
                { id: 'progress-high', label: 'Progress (High)', value: 'progress-high' },
                { id: 'progress-low', label: 'Progress (Low)', value: 'progress-low' }
              ]}
              sortValue={sortBy}
              onSortChange={setSortBy}
              controlsAfterSort={
                <ViewSwitcher
                  currentView={viewMode}
                  onViewChange={setViewMode}
                />
              }
            />
          </div>

          {!hierarchyDefaultsReady ? (
            <div className="mt-6 text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-text)] mx-auto" />
              <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">Loading filters...</p>
            </div>
          ) : viewMode === 'table' ? (
            <div className="mt-6">
              <div className="overflow-x-auto">
              <div className="compact-table min-w-full">
                <FlexibleCRUDTable
                  key={location.pathname} // Force refresh when navigating back
                  entity={transformationEntity}
                  service={transformationServiceWithStats}
                  onView={handleView}
                  onEdit={handleEdit}
                  onCreate={handleCreate}
                  showCreateButton={false}
                  onDelete={handleDelete}
                  useConfirmDialog={true}
                  skipDeleteConfirmation={skipDeleteConfirmation}
                  onDeleteDontAskAgain={() => setSkipDeleteConfirmation(true)}
                  onCopy={handleCopy}
                  onRowClick={handleView}
                  viewActionIcon={EyeIcon}
                  viewActionLabel={t('common.view') || 'View'}
                  editActionIcon={Cog6ToothIcon}
                  editActionLabel={t('transformation.projectSettings') || 'Project settings'}
                  customActions={[
                    {
                      label: t('common.edit') || 'Edit',
                      icon: PencilIcon,
                      onClick: handleUpdate,
                      variant: 'primary'
                    }
                  ]}
                  searchPlaceholder={t('transformation.searchTransformations')}
                  showPagination={true}
                  showSearch={true}
                  pageSize={20}
                  extraQueryParams={(() => {
                  const p: Record<string, string> = {};
                  if (roadmapFilterId) p.roadmap_id = roadmapFilterId;
                  if (lens === 'department') {
                    if (orgHierarchyFilterId) p.organizational_hierarchy_id = orgHierarchyFilterId;
                    if (funcHierarchyFilterId) p.functional_hierarchy_id = funcHierarchyFilterId;
                    if (geoHierarchyFilterId) p.geographic_hierarchy_id = geoHierarchyFilterId;
                  }
                  return Object.keys(p).length ? p : undefined;
                })()}
                />
              </div>
              </div>
            </div>
          ) : (
            <div className="mt-6">
              {cardLoading ? (
                <div className="text-center py-8 mt-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-text)] mx-auto"></div>
                  <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">Loading...</p>
                </div>
              ) : filteredCardData.length === 0 ? (
                <div className="text-center py-8 mt-6">
                  <p className="text-sm text-[var(--color-secondary-text)] opacity-75">
                    {cardData.length === 0
                      ? lens === 'department'
                        ? 'No projects in your department. Check your hierarchy assignment or switch to My projects.'
                        : 'No projects where you are a team member, sponsor, project manager, or stakeholder. Ask your project lead or administrator to add you.'
                      : 'No transformations match your filters'}
                  </p>
                </div>
              ) : (
                <div className="mt-6">
                  <CardGrid columns={4}>
                    {filteredCardData.map((transformation) => (
                      <TransformationCard
                        key={transformation.id}
                        transformation={transformation}
                        onView={handleView}
                        onEdit={handleEdit}
                        onDelete={handleDeleteWithConfirm}
                        onCopy={handleCopy}
                        editActionIcon={Cog6ToothIcon}
                        editActionLabel={t('transformation.projectSettings') || 'Project settings'}
                        customActions={[
                          {
                            label: t('common.edit') || 'Edit',
                            icon: PencilIcon,
                            onClick: handleUpdate,
                            variant: 'primary'
                          }
                        ]}
                      />
                    ))}
                  </CardGrid>
                </div>
              )}
            </div>
          )}

        <ConfirmDialog
          isOpen={!!transformationToDelete}
          onClose={() => setTransformationToDelete(null)}
          onConfirm={handleConfirmDeleteTransformation}
          title={t('messages.confirmDeletePrompt') || 'Are you sure you want to delete?'}
          itemName={transformationToDelete?.name}
          description={t('messages.deleteWarning') || 'This action cannot be undone.'}
          confirmLabel={t('common.delete') || 'Delete'}
          cancelLabel={t('common.cancel') || 'Cancel'}
          variant="danger"
          showDontAskAgain={true}
          dontAskAgainLabel={t('common.dontAskAgain') || "Don't ask again for this session"}
          onDontAskAgainChange={(checked) => checked && setSkipDeleteConfirmation(true)}
        />
      </div>
    </div>
  );
};

export default TransformationsPage; 